import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { MessageSquare, BrainCircuit, FolderOpen, Volume2, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { PasswordSecurity } from "../components/PasswordSecurity";
import { api } from "../lib/apiClient";

const ACCENT = "var(--accent)";
const fmtDay = (d) => {
  const p = (d || "").split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : d;
};

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="hud-panel reticle clip-corner p-4 sm:p-5">
      <span className="rc-tr" />
      <span className="rc-bl" />
      <div className="flex items-center gap-2 text-[#8b949e]">
        <Icon className="h-4 w-4 accent-text" />
        <span className="font-mono-hud text-[9px] tracking-[0.2em] uppercase">{label}</span>
      </div>
      <div className="mt-2 font-display text-3xl font-black text-glow accent-text tabular-nums">{value}</div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="hud-panel clip-corner p-4 sm:p-5">
      <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase mb-4">{title}</h3>
      <div className="h-56">{children}</div>
    </div>
  );
}

const tooltipStyle = {
  background: "rgba(3,10,22,0.95)",
  border: "1px solid var(--accent)",
  borderRadius: 6,
  fontFamily: "Share Tech Mono, monospace",
  fontSize: 12,
  color: "#eaf4ff",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totals = stats?.totals || { messages: 0, memories: 0, documents: 0, voice_chars: 0 };

  return (
    <AppShell title="Tableau de bord" subtitle="Votre activité avec Sara sur les 14 derniers jours">
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin accent-text" />
        </div>
      ) : (
        <div className="space-y-6" data-testid="dashboard-content">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard testid="stat-messages" icon={MessageSquare} label="Conversations" value={totals.messages} />
            <StatCard testid="stat-memories" icon={BrainCircuit} label="Mots mémorisés" value={totals.memories} />
            <StatCard testid="stat-documents" icon={FolderOpen} label="Documents indexés" value={totals.documents} />
            <StatCard testid="stat-voice" icon={Volume2} label="Caractères vocalisés" value={totals.voice_chars.toLocaleString("fr-FR")} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
            <ChartCard title="Conversations dans le temps">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.conversations || []} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gConv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={ACCENT} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
                  <Area type="monotone" dataKey="count" name="Messages" stroke={ACCENT} strokeWidth={2} fill="url(#gConv)" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Usage vocal (caractères)">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.voice || []} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gVoice" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00ffaa" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#00ffaa" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                  <YAxis tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
                  <Area type="monotone" dataKey="chars" name="Caractères" stroke="#00ffaa" strokeWidth={2} fill="url(#gVoice)" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Documents indexés">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.documents_series || []} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="count" name="Documents" fill={ACCENT} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Mots mémorisés dans le temps">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.memories_series || []} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="count" name="Souvenirs" fill="#c084fc" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <PasswordSecurity testid="dashboard-security-panel" />
        </div>
      )}
    </AppShell>
  );
}
